import { Dispatch, SetStateAction } from "react";

import { LangstonParameterSchema } from "@/search/Langston/LangstonParameterSchema";

const useSearchParamsControls = ({
  searchParams,
  setSearchParams,
}: IUseSearchParamsControls) => {
  // Use getAllSearchParams to get a list of all params
  const getAllSearchParams = () => [...searchParams.entries()];

  // Use deleteSearchParamValues() to remove all search param values for the given param name
  const deleteSearchParamValues = (paramName: string) => {
    if (paramName !== LangstonParameterSchema.page.key) {
      searchParams.set(
        LangstonParameterSchema.page.key,
        LangstonParameterSchema.page.default,
      );
    }
    searchParams.delete(paramName);
    setSearchParams(new URLSearchParams(searchParams));
  };

  const deleteSingleParamValue = (paramName: string, paramValue?: string) => {
    //@ts-ignore Second delete argument is supported in later TS versions.
    searchParams.delete(paramName, paramValue);
    setSearchParams(new URLSearchParams(searchParams));
  };

  // Use getSearchParamValues() to get a single value for a given param name
  const getSingleSearchParamValue = (paramName: string) =>
    searchParams.get(paramName) || "";

  // Use getSearchParamValues() to get a list of param values for a given param name
  const getSearchParamValues = (paramName: string) =>
    searchParams.getAll(paramName) || [];

  // Use hasSearchParamValue() to check if a value is selected for a given param name
  const hasSearchParamValue = (paramName: string, paramValue: string) =>
    !!getSearchParamValues(paramName).find((value) => value === paramValue);

  // Use setSearchParamValue() to set or replace value for that param
  const setSearchParamValue = (paramName: string, paramValues: string) => {
    if (paramName !== LangstonParameterSchema.page.key) {
      searchParams.set(
        LangstonParameterSchema.page.key,
        LangstonParameterSchema.page.default,
      );
    }
    searchParams.set(paramName, paramValues);
    setSearchParams(new URLSearchParams(searchParams));
  };

  const appendSearchParam = (paramName: string, paramValue: string) => {
    searchParams.append(paramName, paramValue);
    setSearchParams(new URLSearchParams(searchParams));
  };

  // Use toggleSearchParam() to toggle a param value for a given param name
  // or append multiple values to a param.
  const toggleSearchParamValue = (paramName: string, paramValue: string) => {
    if (paramName !== LangstonParameterSchema.page.key) {
      searchParams.set(
        LangstonParameterSchema.page.key,
        LangstonParameterSchema.page.default,
      );
    }

    const isValueAlreadySelected = hasSearchParamValue(paramName, paramValue);

    if (isValueAlreadySelected) {
      //@ts-ignore Second delete argument is supported in later TS versions.
      searchParams.delete(paramName, paramValue);
    } else {
      appendSearchParam(paramName, paramValue);
      return;
    }
    setSearchParams(new URLSearchParams(searchParams));
  };

  const setMaturityLevelParam = (min: number, max: number) => {
    if (!min && !max) {
      searchParams.delete(LangstonParameterSchema.minimum_maturity.key);
      searchParams.delete(LangstonParameterSchema.maximum_maturity.key);

      setSearchParams(new URLSearchParams(searchParams));
    } else {
      setSearchParamValue(
        LangstonParameterSchema.minimum_maturity.key,
        min.toString(),
      );
      setSearchParamValue(
        LangstonParameterSchema.maximum_maturity.key,
        max.toString(),
      );
    }
  };

  return {
    appendSearchParam,
    deleteSearchParamValues,
    deleteSingleParamValue,
    getAllSearchParams,
    getSearchParamValues,
    getSingleSearchParamValue,
    hasSearchParamValue,
    toggleSearchParamValue,
    setMaturityLevelParam,
    setSearchParamValue,
  };
};

interface IUseSearchParamsControls {
  searchParams: URLSearchParams;
  setSearchParams: Dispatch<SetStateAction<URLSearchParams>>;
}

useSearchParamsControls.displayName = "useSearchParamsControls";
export { useSearchParamsControls };
